// Widget-board defaults — the SINGLE source of truth for every default board a
// widget view starts from. Imported by BOTH sides of the seeding contract:
//   - apps/api/core  — WidgetsService.listByView lazily seeds an uninitialized
//     view's board inside the first read (widget-view-defaults.ts routes a view
//     kind to one of these boards via managedDefaultSeeds/dashboardTemplateSeeds).
//   - apps/client/salestrak-web — the managed catalogs (my-work-widgets.ts,
//     team-overview-widgets.ts) and the template chooser
//     (settings/dashboard-templates.ts) wrap these entries with display copy.
// Because both import the same data, the persisted seeds cannot drift.
//
// PERSISTED IDENTITY — the values here outlive any one build:
//   - `widgetKey` matches a placed widget back to its catalog/template entry
//     (managed migration + idempotent seeding key on it). Never rename one.
//   - Template seed ORDER is identity (`<templateKey>:<index>` widgetKeys are
//     baked from array position). Never reorder an existing template's seeds —
//     append only.
//   - Managed layout versions gate the client's managed-layout migration; bump
//     the version const when a managed board's designed layout changes.
//
// The key unions (MyWorkWidgetKey, …) are the contract: adding a board entry
// means widening its union, which forces the client's presentation maps to
// cover it.

/**
 * One placed widget of a default board — the persisted seed shape (matches
 * the client's `WidgetSeed` and the core API's `WidgetViewSeed`).
 */
export interface WidgetBoardSeed {
  type: string;
  config: Record<string, unknown>;
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * A managed catalog entry: a seed plus its stable identity and whether it is
 * part of the board a first read materializes.
 */
export interface ManagedBoardWidget<K extends string = string> extends WidgetBoardSeed {
  widgetKey: K;
  defaultOn: boolean;
  /**
   * Out of scope for now. The Manage cards panel lists the entry greyed out
   * with no toggle, a first read never seeds it, and the managed migration
   * retires any placed instance. Pairs with `defaultOn: false`.
   */
  disabled?: boolean;
  /**
   * The card's whole verb is the viewer posting Updates — which the Guest
   * rung can never do: Guest's `create.items` registry default is a locked
   * "no", a role floor no share tier lifts. Both sides of the seeding
   * contract read the flag through `boardForViewer`, so for a guest-tier
   * viewer the entry is out of the catalog entirely (no Manage cards row,
   * and the managed migration retires a placed instance) and a first read
   * never seeds it. Viewers above the rung keep the card — a Limited Member
   * posts Updates through edit-tier shares.
   */
  guestHidden?: boolean;
}

/**
 * The board slice a viewer is offered: `guestHidden` entries drop for a
 * viewer at the Guest rung (`isGuestTier` in @salestrak/permissions). Both
 * sides of the seeding contract filter through this — the client's managed
 * catalogs and core's first-read seeding — so a Guest's persisted board and
 * the catalog it reconciles against agree by construction.
 */
export function boardForViewer<K extends string>(
  board: readonly ManagedBoardWidget<K>[],
  viewer: { guestTier: boolean },
): readonly ManagedBoardWidget<K>[] {
  return viewer.guestTier ? board.filter((widget) => !widget.guestHidden) : board;
}

export type MyWorkWidgetKey =
  | "my-work"
  | "needs-attention"
  | "my-activity"
  | "my-pipeline"
  | "notepad"
  | "recent"
  | "my-outcomes"
  | "agenda";

// A retired or re-purposed card ships under a NEW key: the managed migration
// keeps a placed card whose key is still in the catalog, so re-keying retires
// the old card from every existing board and, when the new key is
// `defaultOn`, places its replacement there (`outcomes-v2` retires the old
// members view and places the new summary).
export type TeamOverviewWidgetKey =
  | "team-overview.unassigned"
  | "team-overview.workload"
  | "team-overview.follow-ups"
  | "team-overview.activity"
  | "team-overview.outcomes-v2";

// A template's widgetKeys are `<key>:<index>`, so a template whose widget SET
// changes ships under a NEW key (the `-v2` entries) — reusing a key would give
// old persisted rows a widgetKey that now means a different widget.
export type DashboardTemplateKey =
  | "pipeline-overview-v2"
  | "team-performance-v2"
  | "projects-activity";

export interface DashboardTemplateBoard {
  key: DashboardTemplateKey;
  seeds: WidgetBoardSeed[];
}

/**
 * Age "bad" = Update soon / Stale (`atRisk` / `offTrack`), persisted as an advanced-filter builder
 * state (assignable to the client's `FilterState`). The client compiles it at
 * read time via `resolveWidgetFilters`; the client's typed copy re-exports
 * this one (widget-queries.ts).
 */
export const BAD_AGE_QUERY: {
  logic: "AND";
  blocks: Array<{
    logic: "AND";
    filters: Array<{ field: string; operator: "in"; value: string[] }>;
  }>;
} = {
  logic: "AND",
  blocks: [
    {
      logic: "AND",
      filters: [{ field: "age.status", operator: "in", value: ["atRisk", "offTrack"] }],
    },
  ],
};

/**
 * Stale only — the most-stalled slice of {@link BAD_AGE_QUERY}. Seeded
 * "Off track" counts filter on it; no gallery preset carries it, so a Custom
 * Calculation card reaches the same slice through its Filters.
 */
export const OFF_TRACK_QUERY: typeof BAD_AGE_QUERY = {
  logic: "AND",
  blocks: [
    {
      logic: "AND",
      filters: [{ field: "age.status", operator: "in", value: ["offTrack"] }],
    },
  ],
};

// ── member: My Work (managed catalog) ───────────────────────────────────────
// My Work is a curated personal home, not a reporting dashboard. Its 12-column
// storage grid deliberately uses only 6- and 12-column spans, producing a
// stable two-column rhythm of half- and full-width snapshots.

/** My Work's current managed-layout revision, baked into every placed widget. */
export const MY_WORK_LAYOUT_VERSION = 28;

// Two invariants hold this array. A `widgetKey` never changes and never
// returns once retired: it is baked into every placed widget, and the managed
// migration keys on it. A change to any entry — its span, its seat, its
// config, its `defaultOn` — bumps `MY_WORK_LAYOUT_VERSION`, or no existing
// board picks it up. A retired type stays registered in the app until the
// migration has removed the last placed instance, so a stored row still
// decodes.
export const MY_WORK_BOARD: ManagedBoardWidget<MyWorkWidgetKey>[] = [
  {
    widgetKey: "needs-attention",
    type: "follow-up-list",
    // The queue: the viewer's Project follow ups, banded
    // Stale / Update soon / On track (On track folded), the
    // nearest deadline first, each row leading with its follow-up chip. The
    // card derives the tiers itself, so it carries no filter. "Assigned to me"
    // beside it is the inventory of the same entities (type, co-assignees) and
    // carries no follow-up column, so the two cards never print the same fact
    // twice.
    config: {},
    x: 0,
    y: 0,
    w: 6,
    h: 5,
    defaultOn: true,
    guestHidden: true,
  },
  {
    widgetKey: "my-work",
    type: "assigned-list",
    // The table: the inventory's shape, and in fill layout it holds its
    // columns at the board's half width (a toolbar band with Group by, Sort
    // by, search and the columns gear sits over it).
    config: { sort: "updated", layout: "table", title: "Assigned to me" },
    x: 6,
    y: 0,
    w: 6,
    h: 5,
    defaultOn: true,
  },
  {
    widgetKey: "my-activity",
    type: "activity-list",
    config: { feed: "items", title: "Activity on my work" },
    x: 0,
    y: 11,
    w: 6,
    h: 5,
    defaultOn: true,
  },
  {
    widgetKey: "recent",
    type: "recent-list",
    config: { limit: 10, title: "Recent" },
    x: 6,
    y: 11,
    w: 6,
    h: 5,
    defaultOn: true,
  },
  {
    widgetKey: "my-outcomes",
    type: "outcome-performance",
    config: { view: "summary", title: "My Update outcomes" },
    x: 0,
    y: 5,
    w: 12,
    h: 6,
    defaultOn: true,
    guestHidden: true,
  },
  {
    widgetKey: "notepad",
    type: "note",
    config: { title: "Notepad" },
    x: 0,
    y: 16,
    w: 6,
    h: 4,
    defaultOn: false,
  },
  {
    widgetKey: "my-pipeline",
    type: "dimension-battery",
    config: { groupBy: "stage", title: "My stage mix" },
    x: 6,
    y: 16,
    w: 6,
    h: 4,
    defaultOn: false,
  },
  {
    widgetKey: "agenda",
    type: "agenda",
    config: { title: "Agenda" },
    x: 0,
    y: 20,
    w: 6,
    h: 5,
    defaultOn: false,
    disabled: true,
  },
];

// ── team `-overview`: Team Overview (managed catalog) ───────────────────────
// Team Overview uses the same 6- and 12-column rhythm as My Work. Users can
// switch each visible widget between Half width and Full width.

/** Team Overview's current managed-layout revision. */
export const TEAM_OVERVIEW_LAYOUT_VERSION = 15;

/** A now-state card: every Project that exists, never the board's window. */
const ALL_TIME = { dateRange: { key: "all-time" } };

// One question per card, over the Projects ASSIGNED to the team (its Teams
// property — shared Projects live on the Projects tab):
//   who is doing what      → Workload by assignee (not Done or Canceled)
//   what has no assignee   → Unassigned
//   what is owed, in order → Follow ups (the My Work queue, team-scoped; its
//                            Stale / Update soon bands carry the "needs an
//                            Update" counts, derived live — a separate count
//                            card read the hourly stored status and disagreed)
//   what happened          → Recent activity on the team's Projects
//   how Updates ended    → Update outcomes (full width, under the rest)
// Every card reads fields all kinds share (assignee, follow up, activity), so
// Accounts and Contacts drop in when they carry Teams. Stage categories
// (per-workflow stages lumped together, Projects only) and the Workflows
// roster (reach, not assignment) are retired.
//
// Layout (v15) = Spencer's own arrangement of Team Team 2's overview (Sep 28
// 2026), lifted as the default for every team: Workload tall on the left
// (6×7) with Recent activity under it (6×5); Unassigned on the right at the
// 3-row floor (6×3) with Follow ups under it (6×9); Update outcomes full
// width below (12×6). Heights are composed per card, not one rung.
export const TEAM_OVERVIEW_BOARD: ManagedBoardWidget<TeamOverviewWidgetKey>[] = [
  {
    widgetKey: "team-overview.workload",
    type: "dimension-bar",
    // Untitled on purpose: `dynamicTitle` derives "Workload by assignee".
    config: {
      groupBy: "assignee",
      sort: "volume",
      filters: { ...ALL_TIME, hideClosed: true },
    },
    x: 0,
    y: 0,
    w: 6,
    h: 7,
    defaultOn: true,
  },
  {
    widgetKey: "team-overview.unassigned",
    type: "metric",
    config: {
      measure: "unassigned",
      title: "Unassigned",
      filters: { ...ALL_TIME, hideClosed: true },
    },
    x: 6,
    y: 0,
    w: 6,
    h: 3,
    defaultOn: true,
  },
  {
    widgetKey: "team-overview.follow-ups",
    type: "follow-up-list",
    config: { title: "Follow ups" },
    x: 6,
    y: 3,
    w: 6,
    h: 9,
    defaultOn: true,
  },
  {
    widgetKey: "team-overview.activity",
    type: "activity-list",
    config: { limit: 30, title: "Recent activity" },
    x: 0,
    y: 7,
    w: 6,
    h: 5,
    defaultOn: true,
  },
  {
    widgetKey: "team-overview.outcomes-v2",
    type: "outcome-performance",
    config: { view: "summary", sortBy: "updates", minUpdates: 1, limit: 8, title: "Update outcomes" },
    x: 0,
    y: 12,
    w: 12,
    h: 6,
    defaultOn: true,
  },
];

// ── dashboard templates (team / workflow Dashboard tabs) ────────────────────
// The starting boards the empty-board template chooser offers, and
// the source of the server's team/workflow first-read defaults.

/** Bake `<templateKey>:<index>` widgetKeys into a template's seeds. */
/** The count of Projects created this week, compared with last week. */
const NEW_THIS_WEEK_METRIC = {
  type: "metric",
  config: {
    measure: "total",
    compare: true,
    title: "New this week",
    filters: { dateRange: { key: "this-week" } },
  },
} satisfies Pick<WidgetBoardSeed, "type" | "config">;

/** The Projects by category tile, set to Completed. */
const PROJECTS_CLOSED_CARD = {
  type: "projects-closed",
  config: { category: "done", title: "Projects completed" },
} satisfies Pick<WidgetBoardSeed, "type" | "config">;

function withTemplateKeys(key: DashboardTemplateKey, seeds: WidgetBoardSeed[]): WidgetBoardSeed[] {
  return seeds.map((seed, index) => ({
    ...seed,
    config: { ...seed.config, widgetKey: `${key}:${index}` },
  }));
}

function templateBoard(key: DashboardTemplateKey, seeds: WidgetBoardSeed[]): DashboardTemplateBoard {
  return { key, seeds: withTemplateKeys(key, seeds) };
}

// Each template fills at least a full page of grid rows (48px each) so a
// freshly seeded board never ends in a strip across the top. Seed configs match
// their gallery presets' defaultConfig value-for-value so `inferPreset` binds
// each card the way a gallery placement would; a seed matching an
// `autoTitle: false` preset bakes NO title (the module's `dynamicTitle`
// derives one and tracks setting changes), and one matching a curated-name
// preset bakes that name.
export const DASHBOARD_TEMPLATE_BOARDS: DashboardTemplateBoard[] = [
  // Every CARD opens at the board's one height rung (9 rows = 560px on the
  // dashboard grid); the tiles — metric and projects-closed — sit on the
  // 3-row floor every resizable widget shares (176px).
  templateBoard("pipeline-overview-v2", [
    { type: "metric", config: { measure: "total" }, x: 0, y: 0, w: 3, h: 3 },
    { ...NEW_THIS_WEEK_METRIC, x: 3, y: 0, w: 3, h: 3 },
    {
      type: "metric",
      config: { measure: "total", title: "Update needed", filters: { query: BAD_AGE_QUERY } },
      x: 6,
      y: 0,
      w: 3,
      h: 3,
    },
    { ...PROJECTS_CLOSED_CARD, x: 9, y: 0, w: 3, h: 3 },
    { type: "stage-funnel", config: { rows: "stage" }, x: 0, y: 3, w: 6, h: 9 },
    { type: "series-line", config: { groupBy: "stage" }, x: 6, y: 3, w: 6, h: 9 },
    { type: "stage-conversion", config: { title: "Stage conversion" }, x: 0, y: 12, w: 12, h: 9 },
  ]),
  // Tiles across the top on the 3-row floor, then card rows that each fill
  // all 12 columns on the 9-row rung — follow-up load, workload, the Update
  // needed queue, activity beside conversion, then Update results. Positions
  // only: the seed ORDER (and so every widgetKey) is unchanged.
  templateBoard("team-performance-v2", [
    {
      type: "metric",
      config: { measure: "total", title: "Update needed", filters: { query: BAD_AGE_QUERY } },
      x: 0,
      y: 0,
      w: 4,
      h: 3,
    },
    {
      type: "metric",
      config: { measure: "total", title: "Off track", filters: { query: OFF_TRACK_QUERY } },
      x: 4,
      y: 0,
      w: 4,
      h: 3,
    },
    { type: "dimension-battery", config: { groupBy: "age" }, x: 0, y: 3, w: 6, h: 9 },
    { type: "dimension-bar", config: { groupBy: "assignee" }, x: 6, y: 3, w: 6, h: 9 },
    { type: "activity-list", config: {}, x: 0, y: 21, w: 6, h: 9 },
    {
      type: "entity-list",
      config: { sort: "age", groupBy: "age", title: "Update needed list", filters: { query: BAD_AGE_QUERY } },
      x: 0,
      y: 12,
      w: 12,
      h: 9,
    },
    { ...PROJECTS_CLOSED_CARD, x: 8, y: 0, w: 4, h: 3 },
    { type: "stage-conversion", config: { display: "rate", title: "Conversion rate" }, x: 6, y: 21, w: 6, h: 9 },
    {
      type: "outcome-performance",
      config: { view: "summary", sortBy: "updates", minUpdates: 1, limit: 8 },
      x: 0,
      y: 30,
      w: 12,
      h: 9,
    },
  ]),
  templateBoard("projects-activity", [
    { type: "entity-list", config: { sort: "updated", limit: 25, title: "Projects" }, x: 0, y: 3, w: 8, h: 9 },
    { type: "metric", config: { measure: "total" }, x: 0, y: 0, w: 4, h: 3 },
    { type: "activity-list", config: { limit: 30, title: "Activity" }, x: 8, y: 3, w: 4, h: 9 },
    { ...NEW_THIS_WEEK_METRIC, x: 4, y: 0, w: 4, h: 3 },
    { ...PROJECTS_CLOSED_CARD, x: 8, y: 0, w: 4, h: 3 },
  ]),
];


/**
 * A managed board's server-persisted first-read seeds: the `defaultOn` entries
 * with `widgetKey` and `managedLayoutVersion` baked into config — exactly the
 * shape the client's managed maintenance later matches and migrates by.
 */
export function managedDefaultSeeds(
  board: readonly ManagedBoardWidget[],
  layoutVersion: number,
): WidgetBoardSeed[] {
  return board
    .filter((widget) => widget.defaultOn)
    .map((widget) => ({
      type: widget.type,
      config: { ...widget.config, managedLayoutVersion: layoutVersion, widgetKey: widget.widgetKey },
      x: widget.x,
      y: widget.y,
      w: widget.w,
      h: widget.h,
    }));
}

/**
 * A dashboard template's seeds by key — throws on an unknown key so a typo in
 * a caller fails loudly at boot rather than silently seeding nothing.
 */
export function dashboardTemplateSeeds(key: DashboardTemplateKey): WidgetBoardSeed[] {
  const board = DASHBOARD_TEMPLATE_BOARDS.find((template) => template.key === key);
  if (!board) throw new Error(`Unknown dashboard template: ${key}`);
  return board.seeds;
}
